<script setup lang="ts">
import { computed } from "vue";
import { AwSkeleton } from "@asap/design";
import { achievementStatus, statusColorName } from "./theme";
import type { FunnelBlock } from "./types";

const props = withDefaults(
  defineProps<{
    block?: FunnelBlock;
    title?: string;
    subtitle?: string;
    targetsLabel?: string;
    loading?: boolean;
  }>(),
  { block: undefined, targetsLabel: "Objectifs", loading: false, title: undefined, subtitle: undefined }
);

const frameTitle = computed(() => props.block?.title ?? props.title);
const frameSubtitle = computed(() => props.block?.subtitle ?? props.subtitle);
const leftStep = computed(() => props.block?.steps[0]);
const rightStep = computed(() => props.block?.steps[1]);

const leftAchievementColorName = computed(() =>
  statusColorName(achievementStatus(leftStep.value?.achievementPercent ?? 0))
);
const rightAchievementColorName = computed(() =>
  statusColorName(achievementStatus(rightStep.value?.achievementPercent ?? 0))
);

const ui = {
  stepsGrid: "grid grid-cols-2 gap-2",
  step: "flex flex-col gap-0.5",
  stepRight: "flex flex-col items-end gap-0.5 text-right",
  stepLabel: "text-xs leading-tight text-secondary-700",
  stepValue: "text-lg font-semibold leading-none text-secondary-900",
  chartWrapper: "relative",
  conversionOverlay: "pointer-events-none absolute inset-0 flex items-center justify-center",
  conversionCard: "rounded-lg border border-secondary-200 bg-white px-2.5 py-1.5 text-center",
  conversionLabel: "text-xs leading-tight text-secondary-700",
  conversionValue: "text-base font-semibold text-secondary-900",
  achievementGrid: "grid grid-cols-2 gap-2 text-xs",
  achievement: "font-medium",
  achievementRight: "text-right font-medium",
  targetsGrid: "grid grid-cols-2 gap-2 border-t border-secondary-200 pt-2 text-xs",
  targetsLeft: "flex flex-col gap-0.5",
  targetsLabel: "text-secondary-700",
  targetValue: "font-medium text-secondary-900",
  targetValueRight: "self-end text-right font-medium text-secondary-900",
  footer: "flex flex-col gap-2.5 border-t border-secondary-200 pt-2.5",
  skeletonStepLabel: "h-3 w-28",
  skeletonStepValue: "h-5 w-10",
  skeletonChart: "h-[130px] w-full rounded-lg",
  skeletonConversionLabel: "h-3 w-24",
  skeletonConversionValue: "h-4 w-12 mx-auto",
  skeletonAchievement: "h-3 w-8",
  skeletonTargetLabel: "h-3 w-16",
  skeletonTargetValue: "h-3 w-10",
};
</script>

<template>
  <ConsultantKpiCard :title="frameTitle" :subtitle="frameSubtitle" bordered>
    <div :class="ui.stepsGrid">
      <div :class="ui.step">
        <AwSkeleton v-if="loading" :class="ui.skeletonStepLabel" />
        <span v-else :class="ui.stepLabel">{{ leftStep?.label }}</span>
        <AwSkeleton v-if="loading" :class="ui.skeletonStepValue" />
        <span v-else :class="ui.stepValue">{{ leftStep?.value }}</span>
      </div>
      <div :class="ui.stepRight">
        <AwSkeleton v-if="loading" :class="ui.skeletonStepLabel" />
        <span v-else :class="ui.stepLabel">{{ rightStep?.label }}</span>
        <AwSkeleton v-if="loading" :class="ui.skeletonStepValue" />
        <span v-else :class="ui.stepValue">{{ rightStep?.value }}</span>
      </div>
    </div>

    <div :class="ui.chartWrapper">
      <AwSkeleton v-if="loading" :class="ui.skeletonChart" />
      <slot v-else></slot>

      <div :class="ui.conversionOverlay">
        <div :class="ui.conversionCard">
          <AwSkeleton v-if="loading" :class="ui.skeletonConversionLabel" />
          <div v-else :class="ui.conversionLabel">{{ block?.conversionLabel }}</div>
          <AwSkeleton v-if="loading" :class="ui.skeletonConversionValue" />
          <div v-else :class="ui.conversionValue">{{ block?.conversionValue }}</div>
        </div>
      </div>
    </div>

    <div :class="ui.achievementGrid">
      <AwSkeleton v-if="loading" :class="ui.skeletonAchievement" />
      <span v-else :class="[ui.achievement, `text-${leftAchievementColorName}-600`]">
        {{ leftStep?.achievementPercent }}%
      </span>
      <AwSkeleton v-if="loading" :class="[ui.skeletonAchievement, 'justify-self-end']" />
      <span v-else :class="[ui.achievementRight, `text-${rightAchievementColorName}-600`]">
        {{ rightStep?.achievementPercent }}%
      </span>
    </div>

    <div :class="ui.targetsGrid">
      <div :class="ui.targetsLeft">
        <AwSkeleton v-if="loading" :class="ui.skeletonTargetLabel" />
        <span v-else :class="ui.targetsLabel">{{ targetsLabel }}</span>
        <AwSkeleton v-if="loading" :class="ui.skeletonTargetValue" />
        <span v-else :class="ui.targetValue">{{ leftStep?.targetValue }}</span>
      </div>
      <AwSkeleton v-if="loading" :class="[ui.skeletonTargetValue, 'justify-self-end self-end']" />
      <span v-else :class="ui.targetValueRight">{{ rightStep?.targetValue }}</span>
    </div>

    <div v-if="$slots.footer" :class="ui.footer">
      <slot name="footer"></slot>
    </div>
  </ConsultantKpiCard>
</template>
